/** App staff (/staff): accesso con nome + PIN, vede solo la Gestione Tornei. */
import { useState } from "react";
import { Link, Outlet } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { LogOut } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { errMsg, gare, staffSession, type StaffSession } from "@/lib/gare";

export default function StaffShell() {
  const [session, setSession] = useState<StaffSession | null>(() => staffSession.get());
  const qc = useQueryClient();
  if (!session) {
    return <StaffLogin onLogin={(s) => { staffSession.set(s); qc.clear(); setSession(s); }} />;
  }
  return (
    <div className="min-h-full bg-slate-50">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-5xl items-center justify-between px-4">
          <Link to="/staff" className="flex items-center gap-2.5">
            <img src="https://www.movingpadel.it/img/moving_padel_new.png" alt="Moving Padel Monza" className="h-8 w-auto" />
            <span className="text-sm font-semibold text-slate-900">Tornei</span>
          </Link>
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <span className="hidden sm:inline">Ciao {session.name}</span>
            <button type="button" onClick={() => { staffSession.clear(); qc.clear(); setSession(null); }}
              className="flex items-center gap-1 rounded-md px-2 py-1.5 hover:bg-slate-100" aria-label="Esci">
              <LogOut className="h-4 w-4" /> Esci
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-4 py-5">
        <Outlet />
      </main>
    </div>
  );
}

function StaffLogin({ onLogin }: { onLogin: (s: StaffSession) => void }) {
  const names = useQuery({ queryKey: ["gare", "staff-names"], queryFn: gare.staffNames });
  const [name, setName] = useState("");
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <div className="flex min-h-full items-center justify-center bg-slate-50 px-4">
      <form className="w-full max-w-sm space-y-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr("");
        try {
          onLogin(await gare.login(name, pin));
        } catch (e2) {
          setErr(errMsg(e2));
          setPin("");
        } finally {
          setBusy(false);
        }
      }}>
        <img src="https://www.movingpadel.it/img/moving_padel_new.png" alt="Moving Padel Monza" className="mx-auto h-12 w-auto" />
        <div className="text-center">
          <h1 className="text-lg font-bold text-slate-900">Gestione Tornei</h1>
          <p className="text-sm text-slate-500">Accesso staff</p>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600" htmlFor="lg-name">Chi sei?</label>
          <select id="lg-name" required value={name} onChange={(e) => setName(e.target.value)}
            className="h-12 w-full rounded-md border border-slate-300 bg-white px-3 text-base">
            <option value="" disabled>{names.isLoading ? "Caricamento…" : "Scegli il tuo nome"}</option>
            {(names.data || []).map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          {names.data && !names.data.length ? (
            <p className="mt-1 text-xs text-amber-700">Nessun accesso attivo: chiedi a Davide di impostare il tuo PIN.</p>
          ) : null}
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600" htmlFor="lg-pin">PIN</label>
          <Input id="lg-pin" type="password" inputMode="numeric" autoComplete="current-password" required
            className="h-12 text-center text-xl tracking-[0.5em]" value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))} />
        </div>
        {err ? <p className="text-sm text-red-600" role="alert">{err}</p> : null}
        <Button type="submit" size="lg" className="w-full" loading={busy} disabled={!name || pin.length < 4}>Entra</Button>
      </form>
    </div>
  );
}
